import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { MessagesLink } from "@/features/messaging";
import { NotificationBell } from "@/features/notifications";
import { usePermissions } from "@/features/auth";

/**
 * Liens de session dans l'en-tête.
 *
 * Rien n'est affiché tant que le statut est `unknown` : montrer « Se connecter »
 * puis le remplacer par le nom de l'utilisateur une demi-seconde plus tard
 * donne l'impression d'une application qui se déconnecte toute seule.
 */
export function SessionNav() {
  const { t } = useTranslation(["auth", "account", "learning", "catalog"]);
  const { can, isAuthenticated } = usePermissions();

  if (!isAuthenticated) {
    return (
      <Link to="/login" className="text-brand-600 text-sm font-medium hover:underline">
        {t("auth:login.submit")}
      </Link>
    );
  }

  // « Mes cours » et le tableau de bord sont proposés à tout compte connecté,
  // sans filtrer sur une permission : un enseignant peut suivre un cours, et
  // masquer un lien sur un rôle rendrait l'écran introuvable à qui y a droit.
  // Les deux sont vides pour qui n'est inscrit nulle part — un écran vide se
  // comprend, un lien absent ne s'explique pas.
  //
  // Le tableau de bord d'enseignant, lui, n'est **pas** ici : il n'aurait rien
  // à dire à un élève, et le serveur lui répondrait 403. Il est atteint depuis
  // l'espace enseignant (le lien ci-dessous), où l'on sait déjà pourquoi on
  // est.
  //
  // Ce lien-ci, en revanche, EST filtré sur `catalog.course.create` : à la
  // différence de « suivre un cours », créer un cours a une permission
  // dédiée, et tout compte connecté ne l'a pas. Le masquer ne rend rien
  // introuvable à qui y a droit — seulement inutile à qui ne l'a pas.
  return (
    <>
      <Link to="/learn/dashboard" className="text-brand-600 text-sm font-medium hover:underline">
        {t("learning:dashboard.title")}
      </Link>
      <Link to="/learn" className="text-brand-600 text-sm font-medium hover:underline">
        {t("learning:mine.title")}
      </Link>
      {/* Sans permission, comme « mes cours » : une attestation appartient à
          celui qui l'a obtenue, et la liste d'un compte qui n'en a aucune est
          vide plutôt qu'interdite. */}
      <Link to="/learn/certificates" className="text-brand-600 text-sm font-medium hover:underline">
        {t("learning:certificates.nav")}
      </Link>
      {can("catalog.course.create") && (
        <Link to="/teach/courses" className="text-brand-600 text-sm font-medium hover:underline">
          {t("catalog:nav.myCourses")}
        </Link>
      )}
      <Link to="/account" className="text-brand-600 text-sm font-medium hover:underline">
        {t("account:heading")}
      </Link>
      {/* Sans permission ni filtre : les notifications sont personnelles, et
          tout compte connecté en a. C'est aussi la cloche qui ouvre le flux SSE,
          d'où sa présence sur toutes les pages authentifiées plutôt que sur la
          seule page des notifications — la brancher là-bas ne la tiendrait
          ouverte que pendant qu'on la regarde. */}
      {/* Sans permission non plus : `messaging.message.read_own` est portée par
          les quatre rôles système, et masquer l'entrée sur une permission que
          tout le monde a coûterait un `can()` pour un `true` constant. Le
          serveur, lui, la vérifie — un écran caché ne protège rien.
          ⚠️ Ce « tout le monde » a été faux : `admin` et `moderator` ne l'ont
          reçue qu'en `202609070900`/`202609071000`, et le badge répondait 403 sur
          chaque page en se contentant d'afficher zéro. C'est
          `test_every_system_role_can_read_its_own_messages` qui tient désormais
          l'affirmation, et non ce commentaire. */}
      <MessagesLink />
      <NotificationBell />
    </>
  );
}
